import { Box, Button, Divider, Slide, Stack } from "@mui/material";

import { ViewList, ViewModule } from "@mui/icons-material";
import { ContractsTable } from "../contracts/views/ContractsTable";
import { ContractsGrid } from "../contracts/views/ContractsGrid";
import {
  Breadcrumbs,
  ButtonRow,
  DataGridPage,
  EContractState,
  FlexLoadingBlock,
  SearchMultipleField,
  SwitchField,
  ToggleField,
  useSearchParamsBoolean,
  useSearchParamsForm,
  useSearchParamsOptionsArray,
  useStateStorageJson,
} from "@synota-io/synota-shared-ui";
import { IS_PRODUCTION_ENVIRONMENT } from "../../../utils/environment";
import { useContracts } from "../../../api/hooks/useContracts";
import { useAuth } from "../../../api/auth/useAuth";
import { useContractLinks } from "../contracts/hooks/useContractLinks";

type ContractListSettings = {
  view: "list" | "grid";
};

type ContractListFilters = {
  customers_only: boolean | null;
  filter_overdue: boolean | null;
  show_hidden: boolean | null;
  seconds_since_last_invoice?: { value: number; label?: string };
  contract_states?: Array<{ value: string; label?: string }>;
};

const STATUS_OPTIONS = Object.values(EContractState).map((status) => ({
  label: status.toString(),
  value: status.toString(),
}));

const LAST_INVOICE_OPTIONS = [
  { label: "> 24 hours", value: 60 * 60 * 24 },
  { label: "> 48 hours", value: 60 * 60 * 24 * 2 },
];

export const BUTTON_ROW_HEIGHT = "64px";

export const ContractsPage = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useStateStorageJson<ContractListSettings>(
    "contract-list-settings",
    {
      view: "list",
    },
  );

  const filterForm = useSearchParamsForm<ContractListFilters>(
    {
      defaultValues: {
        contract_states: STATUS_OPTIONS.filter((o) => o.value !== EContractState.Completed),
        customers_only: IS_PRODUCTION_ENVIRONMENT || false,
        filter_overdue: false,
        show_hidden: false,
        seconds_since_last_invoice: LAST_INVOICE_OPTIONS[0],
      },
    },
    {
      contract_states: useSearchParamsOptionsArray(),
      customers_only: useSearchParamsBoolean(),
      filter_overdue: useSearchParamsBoolean(),
      show_hidden: useSearchParamsBoolean(),
      seconds_since_last_invoice: {
        deserialize(value) {
          return LAST_INVOICE_OPTIONS.find((option) => option.value === Number(value));
        },
        serialize(value) {
          return value ? value.value.toString() : "";
        },
      },
    },
  );

  const values = filterForm.watch();

  const contractsReturn = useContracts(
    isAdmin
      ? {
          contract_states: values.contract_states?.length
            ? values.contract_states.map((status) => status.value as EContractState)
            : undefined,
          customers_only: values.customers_only || false,
          show_hidden: values.show_hidden || false,
          seconds_since_last_invoice: values.filter_overdue
            ? values.seconds_since_last_invoice?.value
            : undefined,
        }
      : {},
  );

  const { matchedLinks } = useContractLinks();

  const toolbar = (
    <ButtonRow flexWrap={{ xs: "wrap", lg: "nowrap" }}>
      {isAdmin ? (
        <>
          <Box width={{ xs: "100%", lg: "auto" }}>
            <SearchMultipleField
              size="small"
              fullWidth
              label="Filter by status"
              options={STATUS_OPTIONS}
              control={filterForm.control}
              name="contract_states"
            />
          </Box>
          <SwitchField
            label="Filter overdue contracts"
            control={filterForm.control}
            name="filter_overdue"
          />
          <Slide in={!!values.filter_overdue}>
            <span>
              <ToggleField
                size="small"
                color={values.filter_overdue ? "primary" : "standard"}
                disabled={!values.filter_overdue}
                control={filterForm.control}
                name="seconds_since_last_invoice"
                options={LAST_INVOICE_OPTIONS}
              />
            </span>
          </Slide>
        </>
      ) : (
        <Breadcrumbs links={matchedLinks} />
      )}
      <Stack flexGrow={1} />
      <Stack
        direction="row"
        flexGrow={1}
        justifyContent="end"
        gap={4}
        width={{ xs: "100%", md: "auto" }}
      >
        {isAdmin ? (
          <Stack
            gap={4}
            flexGrow={1}
            direction="row"
            justifyContent={{ xs: "start", lg: "end" }}
            alignItems="center"
          >
            <SwitchField label="Show hidden" control={filterForm.control} name="show_hidden" />
            <SwitchField
              label="Customers only"
              control={filterForm.control}
              name="customers_only"
            />
          </Stack>
        ) : null}
        <Stack direction="row">
          <Button
            size="small"
            title="List view"
            onClick={() => setSettings((curr) => ({ ...curr, view: "list" }))}
            color={settings?.view === "list" ? "primary" : "neutral"}
          >
            <ViewList fontSize="small" />
          </Button>
          <Divider orientation="vertical" />
          <Button
            size="small"
            title="Grid view"
            onClick={() => setSettings((curr) => ({ ...curr, view: "grid" }))}
            color={settings?.view === "grid" ? "primary" : "neutral"}
          >
            <ViewModule fontSize="small" />
          </Button>
        </Stack>
      </Stack>
    </ButtonRow>
  );

  const content =
    settings?.view === "grid" ? (
      <ContractsGrid {...contractsReturn} />
    ) : (
      <ContractsTable {...contractsReturn} />
    );

  return (
    <DataGridPage
      disablePaper={settings?.view === "grid"}
      recalculateHeightDeps={values}
      toolbar={toolbar}
    >
      {contractsReturn.isLoading ? (
        <FlexLoadingBlock>Loading Contracts...</FlexLoadingBlock>
      ) : (
        content
      )}
    </DataGridPage>
  );
};
